import { Alert } from "@/components/ui/alert";
import { CardAlert } from "@/utils";
import React from "react";
import { AlertCircle, LifeBuoy, X } from "lucide-react";

interface Props {
  alert: CardAlert;
  clearAlert: () => void;
  onClickAction: (id?: string) => void;
}

export function SocialAlert({ alert, clearAlert, onClickAction }: Props) {
  return (
    <div className="transition-all animate-in fade-in zoom-in-95 duration-200 ease-out shadow-sm rounded-lg">
      <Alert
        variant={alert.type === "error" ? "destructive" : "default"}
        className={`
          relative flex items-start gap-3 p-4 border-2
          ${alert.type === "error" ? "bg-red-50/50 border-red-200 text-red-900" : ""}
          ${alert.type === "warning" ? "bg-[#FFFCF3] border-amber-200/80 text-amber-900" : ""}
          ${alert.type === "success" ? "bg-emerald-50/50 border-emerald-200 text-emerald-900" : ""}
        `}
      >
        {/* Icon: Align to the top line of text */}
        <AlertCircle className="h-4 w-4 shrink-0 mt-[2px] opacity-80" />

        {/* Content Wrapper */}
        <div className="flex-1 flex flex-col gap-3 pr-6">
          {/* Text Block */}
          <div className="flex flex-col gap-1">
            <div className="text-md font-medium leading-snug tracking-tight">{alert.message}</div>
            {alert.description && <div className="text-sm opacity-80 leading-relaxed">{alert.description}</div>}
          </div>

          {/* Action Block */}
          {alert.action && (
            <button
              onClick={() => {
                onClickAction(alert.action?.id);
                clearAlert();
              }}
              className="
                flex w-fit items-center gap-1.5 px-3 py-1.5
                bg-white border border-slate-200 rounded-full
                text-sm font-medium text-slate-600 shadow-sm transition-all
                hover:text-slate-900 hover:bg-slate-50 hover:border-slate-300
                active:scale-95 focus:outline-none focus:ring-2 focus:ring-slate-100
              "
            >
              {/* Only show the LifeBuoy if it's the help action */}
              {alert.action.id === "SHOW_HELP" && <LifeBuoy size={14} className="text-blue-500" />}
              {alert.action.label}
            </button>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={clearAlert}
          className="absolute right-3 top-3 rounded-md p-1 opacity-40 transition-opacity hover:bg-black/5 hover:opacity-100"
        >
          <X size={15} />
          <span className="sr-only">Close alert</span>
        </button>
      </Alert>
    </div>
  );
}
